import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { withSearch } from '../utils/attribution';

// The signed-out homepage (2026-10 restyle, after linear.app). One idea per
// screen, the product as the hero, hairlines instead of boxes, and the fewest
// words that still say it. Two funnel entries ride the UTMs: /start (connect a
// device) and /quiz (supplements).
//
// Everything drawn here is illustration — static example cards, not the
// visitor's data. The real cards live behind sign-in.

const Wrap = ({ className = '', children }) => (
  <div className={`mx-auto max-w-[1200px] px-4 sm:px-6 ${className}`}>{children}</div>
);

const Dot = ({ tone }) => (
  <i className={`inline-block h-2 w-2 rounded-full ${tone === 'g' ? 'bg-good-strong dark:bg-good' : tone === 'r' ? 'bg-off-strong dark:bg-off' : 'bg-fg-3'}`} />
);

// A static example card in the Home grammar: kind → metric → big number → sentence → chart.
const ExampleCard = ({ kind, tone, metric, big, small, dir, say, children, featured = false, className = '' }) => (
  <div className={`flex min-w-0 flex-col rounded-xl border border-line bg-raised ${featured ? 'p-5' : 'p-4'} ${className}`}>
    <span className="inline-flex items-center gap-2 text-sm font-medium text-fg-3"><Dot tone={tone} />{kind}</span>
    <span className="mt-1 font-medium text-fg-2">{metric}</span>
    {big && (
      <span className={`mt-1 flex items-baseline gap-2 font-medium tabular-nums tracking-[-0.03em] leading-none ${featured ? 'text-[2.6rem]' : 'text-[2rem]'}`}>
        {dir && <span className={`font-normal ${featured ? 'text-[1.6rem]' : 'text-[1.3rem]'} ${tone === 'g' ? 'text-good-strong dark:text-good' : tone === 'r' ? 'text-off-strong dark:text-off' : 'text-fg-3'}`} aria-hidden="true">{dir === 'up' ? '↑' : '↓'}</span>}
        {big}{small && <small className="text-base font-normal tracking-normal text-fg-3">{small}</small>}
      </span>
    )}
    <span className={`mt-2 font-medium leading-snug tracking-[-0.01em] ${featured ? 'text-[1.15rem]' : 'text-[1.05rem]'}`}>{say}</span>
    {children}
  </div>
);

const Pill = ({ to, children, lg = false }) => (
  <Link to={to} className={`pill-cta ${lg ? '!text-[1.05rem] !px-[26px] !py-[14px]' : ''}`}>{children}</Link>
);

const LandingPage = () => {
  const { search } = useLocation();
  const start = withSearch('/start', search);
  const quiz = withSearch('/quiz', search);

  return (
    <div className="pb-24">
      {/* ---- Hero: one line, one button, the product ---- */}
      <section className="relative pt-14 pb-12 lg:pt-20">
        <div className="lite-bottom" aria-hidden="true" />
        <Wrap className="lite-above">
          <h1 className="max-w-[14em] text-[clamp(2.5rem,6.2vw,4.3rem)] font-medium leading-[1.02] tracking-[-0.025em]">
            See what’s moving in your health.
          </h1>
          <p className="mt-9 max-w-[34em] text-[1.1rem] text-fg-3">
            Connect the watch you already wear. We show you what changed, in plain words, with the chart right there.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Pill to={start} lg>Connect a device</Pill>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {['Fitbit', 'Oura', 'Withings', 'Android', 'Free'].map((d) => (
              <span key={d} className="rounded-full border border-line px-3.5 py-1.5 text-sm text-fg-2">{d}</span>
            ))}
          </div>

          {/* Product frame */}
          <div className="relative mt-16">
          <div className="grid overflow-hidden rounded-2xl border border-line-strong bg-frame shadow-[0_30px_80px_-30px_rgba(0,0,0,.6)] md:grid-cols-[220px_1fr]" aria-label="Preview of the Home page">
            <aside className="hidden flex-col gap-1 border-r border-line p-3.5 text-sm md:flex">
              <div className="px-2.5 pb-3.5 pt-1.5 font-[700] tracking-[-0.03em]">Vegetablah</div>
              {['Home', 'You', 'Log', 'Data', 'Insights', 'Supplements'].map((n, i) => (
                <span key={n} className={`flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] ${i === 0 ? 'bg-elev text-fg' : 'text-fg-2'}`}>
                  <i className={`h-[7px] w-[7px] rounded-full ${i === 0 ? 'bg-fg' : 'bg-fg-3'}`} />{n}
                </span>
              ))}
            </aside>
            <div className="min-w-0 p-4 sm:p-5">
              <div className="pb-3 text-sm text-fg-3"><b className="text-[1.05rem] font-[600] text-fg">What’s moving</b> &nbsp;·&nbsp; your top 3 this week</div>
              <div className="grid gap-3 lg:grid-cols-[2fr_1fr] lg:grid-rows-[auto_auto]">
                <ExampleCard featured className="lg:row-span-2" kind="Since you started magnesium" tone="g" metric="Resting heart rate" big="3" small="beats lower" dir="down" say="Your heart has been beating a little slower at rest since Feb 25.">
                  <div className="mt-3">
                    <svg viewBox="0 0 600 220" preserveAspectRatio="none" className="block h-[220px] w-full overflow-visible" aria-hidden="true">
                      <rect x="300" y="0" width="300" height="220" fill="var(--you-soft)" />
                      <line x1="0" y1="78" x2="300" y2="78" stroke="var(--fg-3)" strokeWidth="1" strokeDasharray="4 4" />
                      <line x1="300" y1="118" x2="600" y2="118" stroke="var(--fg-3)" strokeWidth="1" strokeDasharray="4 4" />
                      <line x1="300" y1="0" x2="300" y2="220" stroke="var(--line-strong)" strokeWidth="1" />
                      <polyline fill="none" stroke="var(--you)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" points="0,82 25,70 50,90 75,76 100,84 125,66 150,88 175,74 200,80 225,68 250,86 275,72 300,80 320,104 340,124 360,110 380,130 400,114 420,122 440,108 460,128 480,116 500,120 520,106 540,126 560,112 580,118 600,116" />
                      <circle cx="600" cy="116" r="4" fill="var(--you)" />
                    </svg>
                    <div className="mt-1.5 flex justify-between text-xs text-fg-3 tabular-nums"><span>6 weeks before</span><span>Started magnesium</span><span>6 weeks after</span></div>
                  </div>
                </ExampleCard>
                <ExampleCard kind="Last night" tone="r" metric="Blood oxygen" big="8×" small="more up and down" dir="up" say="It usually holds steady. Last night it didn’t.">
                  <svg viewBox="0 0 300 72" preserveAspectRatio="none" className="mt-3 block h-[72px] w-full overflow-visible" aria-hidden="true"><line x1="0" y1="52" x2="300" y2="52" stroke="var(--fg-3)" strokeWidth="1" strokeDasharray="4 4" /><polyline fill="none" stroke="var(--you)" strokeWidth="2" strokeLinejoin="round" points="0,53 25,50 50,54 75,49 100,53 125,48 150,52 175,45 200,53 225,50 250,54 275,47 300,8" /><circle cx="300" cy="8" r="3.5" fill="var(--you)" /></svg>
                </ExampleCard>
                <ExampleCard kind="Night before last" tone="r" metric="Sleep score" big="42" small="out of 100" dir="down" say="You’re usually around 78. That was a rough one.">
                  <svg viewBox="0 0 300 72" preserveAspectRatio="none" className="mt-3 block h-[72px] w-full overflow-visible" aria-hidden="true"><line x1="0" y1="22" x2="300" y2="22" stroke="var(--fg-3)" strokeWidth="1" strokeDasharray="4 4" /><polyline fill="none" stroke="var(--you)" strokeWidth="2" strokeLinejoin="round" points="0,20 25,24 50,18 75,23 100,19 125,25 150,21 175,17 200,24 225,20 250,23 275,19 300,64" /><circle cx="300" cy="64" r="3.5" fill="var(--you)" /></svg>
                </ExampleCard>
              </div>
            </div>
          </div>
          </div>
        </Wrap>
      </section>

      {/* ---- Statement ---- */}
      <section className="py-16 sm:py-24">
        <Wrap>
          <h2 className="max-w-[22em] text-[clamp(1.7rem,3.6vw,2.7rem)] font-medium leading-[1.15] tracking-[-0.022em]">
            Your watch already collects the numbers. <span className="text-fg-3">We tell you what changed.</span>
          </h2>
        </Wrap>
      </section>

      {/* ---- Three kinds of card ---- */}
      <section className="pb-16 sm:pb-24">
        <Wrap>
          <div className="grid gap-3.5 md:grid-cols-3">
            <div className="min-w-0 rounded-xl border border-line p-5">
              <div className="text-sm font-medium text-fg-3">Today</div>
              <div className="mt-1.5 text-[1.1rem] font-medium leading-[1.3] tracking-[-0.01em]">“You slept 5 hours. You usually get 7.”</div>
              <svg viewBox="0 0 300 80" preserveAspectRatio="none" className="mt-3.5 block h-20 w-full overflow-visible" aria-hidden="true"><line x1="0" y1="30" x2="300" y2="30" stroke="var(--fg-3)" strokeWidth="1" strokeDasharray="4 4" /><polyline fill="none" stroke="var(--you)" strokeWidth="2.5" strokeLinejoin="round" points="0,32 40,26 80,34 120,28 160,33 200,27 240,31 300,70" /><circle cx="300" cy="70" r="4" fill="var(--you)" /></svg>
            </div>
            <div className="min-w-0 rounded-xl border border-line p-5">
              <div className="text-sm font-medium text-fg-3">Something changed</div>
              <div className="mt-1.5 text-[1.1rem] font-medium leading-[1.3] tracking-[-0.01em]">“Your resting heart rate has been 3 beats higher for 10 days.”</div>
              <svg viewBox="0 0 300 80" preserveAspectRatio="none" className="mt-3.5 block h-20 w-full overflow-visible" aria-hidden="true"><line x1="0" y1="50" x2="150" y2="50" stroke="var(--fg-3)" strokeWidth="1" strokeDasharray="4 4" /><line x1="150" y1="22" x2="300" y2="22" stroke="var(--fg-3)" strokeWidth="1" strokeDasharray="4 4" /><polyline fill="none" stroke="var(--you)" strokeWidth="2.5" strokeLinejoin="round" points="0,52 30,46 60,54 90,48 120,52 150,44 180,26 210,20 240,24 270,18 300,22" /><circle cx="300" cy="22" r="4" fill="var(--you)" /></svg>
            </div>
            <div className="min-w-0 rounded-xl border border-line p-5">
              <div className="text-sm font-medium text-fg-3">A pattern</div>
              <div className="mt-1.5 text-[1.1rem] font-medium leading-[1.3] tracking-[-0.01em]">“You sleep 40 minutes less on weekends.”</div>
              <svg viewBox="0 0 300 80" preserveAspectRatio="none" className="mt-3.5 block h-20 w-full overflow-visible" aria-hidden="true"><g fill="var(--fg-2)"><rect x="10" y="20" width="26" height="60" /><rect x="46" y="16" width="26" height="64" /><rect x="82" y="24" width="26" height="56" /><rect x="118" y="18" width="26" height="62" /><rect x="154" y="22" width="26" height="58" /></g><g fill="var(--you)"><rect x="210" y="44" width="26" height="36" /><rect x="246" y="40" width="26" height="40" /></g></svg>
            </div>
          </div>
        </Wrap>
      </section>

      {/* ---- Layer 2: supplements ---- */}
      <section>
        <Wrap>
          <div className="grid items-center gap-8 border-y border-line py-14 sm:py-[72px] md:grid-cols-2 md:gap-12">
            <div>
              <h2 className="text-[clamp(1.6rem,3vw,2.3rem)] font-medium leading-[1.15] tracking-[-0.025em]">Add what you take. See what moved after.</h2>
              <p className="mt-4 max-w-[34em] text-[1.1rem] text-fg-3">Type the name once. We watch the weeks after you started.</p>
              <Link to={quiz} className="mt-5 inline-block text-sm font-medium text-fg-2 hover:text-fg">Not sure what to take? Take the quiz →</Link>
            </div>
            <div className="rounded-xl border border-line-strong bg-frame px-[22px] py-5 text-sm" aria-label="Example supplement stack">
              {[
 ['Magnesium', 'day 18', '↓ heart rate 3 beats', 'text-good-strong dark:text-good'],
 ['Vitamin D', 'day 42', 'nothing moved', 'text-fg-3'],
 ['Creatine', 'day 6', 'too early', 'text-fg-3'],
              ].map(([n, s, d, tone], i) => (
                <div key={n} className={`grid grid-cols-[1fr_auto] items-center gap-3 py-3 ${i < 2 ? 'border-b border-line' : ''}`}>
                  <div><div className="text-base font-medium">{n}</div><div className="text-fg-3">{s}</div></div>
                  <div className={`font-[600] tabular-nums ${tone}`}>{d}</div>
                </div>
              ))}
            </div>
          </div>
        </Wrap>
      </section>

      {/* ---- Three steps ---- */}
      <section className="relative py-16 sm:py-24">
        <div className="lite-spot left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" aria-hidden="true" />
        <Wrap className="lite-above">
          <h2 className="text-[clamp(1.6rem,3vw,2.3rem)] font-medium leading-[1.15] tracking-[-0.025em]">Three steps. First card tomorrow morning.</h2>
          <div className="mt-12 grid gap-4 md:grid-cols-3">
            <div className="min-w-0 overflow-hidden rounded-xl border border-line-strong bg-frame">
              <div className="flex min-h-[190px] flex-col justify-center gap-2.5 p-[18px]">
                {['Fitbit', 'Oura', 'Withings', 'Android'].map((d, i) => (
                  <div key={d} className={`flex items-center justify-between rounded-[10px] border bg-raised px-3.5 py-2.5 text-sm ${i === 0 ? 'border-fg-3' : 'border-line'}`}>{d}<span className="text-fg-3">→</span></div>
                ))}
              </div>
              <div className="border-t border-line px-[18px] py-4 text-[1.05rem] font-medium">1. Pick your watch</div>
            </div>
            <div className="min-w-0 overflow-hidden rounded-xl border border-line-strong bg-frame">
              <div className="flex min-h-[190px] flex-col justify-center p-[18px]">
                <div className="rounded-[10px] border border-line bg-raised p-4 text-center text-sm">
                  Let Vegetablah read your sleep, heart rate and steps?
                  <div className="mt-3"><span className="pill-cta !px-3.5 !py-[7px] !text-xs">Allow</span></div>
                </div>
              </div>
              <div className="border-t border-line px-[18px] py-4 text-[1.05rem] font-medium">2. Tap Allow</div>
            </div>
            <div className="min-w-0 overflow-hidden rounded-xl border border-line-strong bg-frame">
              <div className="flex min-h-[190px] flex-col justify-center p-[18px]">
                <ExampleCard kind="Last night" metric="Sleep" big="6.8" small="hours" say="Your first night. Your normal starts here." className="!p-4 [&>span:last-child]:!text-sm [&>span:last-child]:!font-normal [&>span:last-child]:!text-fg-3" />
              </div>
              <div className="border-t border-line px-[18px] py-4 text-[1.05rem] font-medium">3. Wake up to your first card</div>
            </div>
          </div>
        </Wrap>
      </section>

      {/* ---- Trust ---- */}
      <section className="pb-16 sm:pb-24">
        <Wrap>
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <h2 className="text-[clamp(1.6rem,3vw,2.3rem)] font-medium leading-[1.15] tracking-[-0.025em]">Why you can trust it</h2>
              <ul className="mt-6 flex flex-col">
                {[
 ['Compared to you, not to everyone.', 'Your own last 30 days are the yardstick.'],
 ['No data, no card.', 'If the watch was off, we wait instead of guessing.'],
 ['The numbers are on the card.', 'You can check every claim where it’s made.'],
                ].map(([b, s], i) => (
                  <li key={b} className={`border-t border-line py-[18px] text-[1.15rem] font-medium tracking-[-0.01em] ${i === 2 ? 'border-b' : ''}`}>
                    {b}<span className="mt-1 block text-base font-normal text-fg-3">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <ExampleCard kind="Waiting" metric="Resting heart rate" say="Not enough nights yet. 4 of the last 10 were recorded." className="opacity-85">
              <div className="mt-3.5 flex gap-[3px]" aria-label="4 of 10 nights recorded">
                {[1, 0, 0, 1, 0, 0, 1, 0, 0, 1].map((on, i) => <i key={i} className={`h-2 flex-1 rounded-sm ${on ? 'bg-fg' : 'bg-line-strong'}`} />)}
              </div>
              <div className="mt-1.5 flex justify-between text-xs text-fg-3"><span>10 nights ago</span><span>last night</span></div>
            </ExampleCard>
          </div>
        </Wrap>
      </section>

      {/* ---- Four things we never say ---- */}
      <section className="pb-16 sm:pb-24">
        <Wrap>
          <h2 className="text-[clamp(1.6rem,3vw,2.3rem)] font-medium leading-[1.15] tracking-[-0.025em]">Four things we never say</h2>
          <div className="mt-8 grid gap-x-12 md:grid-cols-2">
            {[
 ['“This caused that.”', 'We only say what moved at the same time.'],
 ['“You should take this.”', 'You add what you take. We just watch.'],
 ['“You have a condition.”', 'Not a doctor. If a number worries you, see one.'],
 ['“Pay to see the rest.”', 'All of it is free. Your data is yours.'],
            ].map(([b, p]) => (
              <div key={b} className="grid grid-cols-[28px_1fr] gap-3.5 border-t border-line py-4">
                <div className="text-[1.2rem] leading-[1.3] text-off-strong dark:text-off" aria-hidden="true">×</div>
                <div><b className="block text-[1.1rem] font-medium">{b}</b><p className="mt-1 text-fg-3">{p}</p></div>
              </div>
            ))}
          </div>
        </Wrap>
      </section>

      {/* ---- Who + final CTA ---- */}
      <section>
        <Wrap>
          <div className="grid grid-cols-[72px_1fr] items-center gap-6 border-y border-line py-10">
            <div className="grid h-[72px] w-[72px] place-items-center rounded-full border border-line-strong bg-elev font-[600] text-fg-2" aria-hidden="true">CW</div>
            <div>
              <b className="block font-medium">Made by Curtis White, one person reading his own watch data honestly.</b>
              <Link to="/about" className="mt-1.5 inline-block text-sm font-medium text-fg-2 hover:text-fg">About the project →</Link>
            </div>
          </div>
          <div className="mt-12 flex flex-wrap gap-3"><Pill to={start} lg>Connect a device</Pill></div>
        </Wrap>
      </section>
    </div>
  );
};

export default LandingPage;
